import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Modal,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Focus,
  LoaderCircle,
  RefreshCcw,
  Star,
  Target,
  UserRound,
  Users,
  X,
} from "lucide-react";
import {
  formatAssessmentQuestionIndexLabel,
  resolveDashboardQuestionIndex,
  resolveDashboardQuestionPreviewContent,
  type QuestionPreviewSegment,
} from "../../../utils/assessmentInteractive";
import {
  formatAssessmentScoreNumber,
  getAssessmentBaseScore,
} from "../../../utils/assessmentScore";
import { AssessmentScoreInline } from "../assessment-score-inline";
import {
  AssessmentManagerDashboardData,
  AssessmentManagerDashboardParticipant,
  AssessmentManagerDashboardQuestionRef,
  AssessmentManagerDashboardQuestionSnapshot,
  getAssessmentManagerDashboard,
} from "../../../axios/quiz";
import useUserStore from "../../../stores/userStore";
import {
  assessmentFocusLabelStyle,
  currentQuestionCardStyle,
  attentionBadgeInactiveStyle,
  attentionBadgeStuckStyle,
  attentionListStyle,
  bottomPanelFooterStyle,
  bottomPanelHeaderStyle,
  bottomPanelStyle,
  bottomPanelTitleMutedStyle,
  bottomPanelTitleStyle,
  bottomSectionGridStyle,
  currentQuestionMetaStyle,
  currentQuestionTitleStyle,
  dashboardGridStyle,
  overviewMiddleRowStyle,
  metricMiniCardIconWrapStyle,
  metricMiniCardIconWrapSuccessStyle,
  metricMiniCardLabelStyle,
  metricMiniCardStyle,
  metricMiniCardValueStyle,
  metaTextStyle,
  metricsRowStyle,
  modalStyle,
  performanceBarFillStyle,
  performanceBarTrackStyle,
  performanceMetricsRowStyle,
  performancePercentStyle,
  performanceQuestionStyle,
  performanceQuestionTopRowStyle,
  performanceRowHighlightStyle,
  performanceRowStyle,
  performanceStatusIconStyle,
  performanceTableHeaderCellStyle,
  performanceTableHeaderStyle,
  performanceTypeChipStyle,
  progressMiniCardStyle,
  progressMiniGridStyle,
  progressMiniScrollWrapStyle,
  progressMiniRingLabelStyle,
  progressMiniRingProgressStyle,
  progressMiniRingTrackStyle,
  needsAttentionHeaderStyle,
  needsAttentionPanelCombinedStyle,
  needsAttentionRowStyle,
  participantAvatarStyle,
  participantCellStyle,
  participantIdentityStyle,
  participantMutedCellStyle,
  participantNameStyle,
  participantStatusCompletedStyle,
  participantStatusInProgressStyle,
  participantStatusNotStartedStyle,
  participantsTableHeaderCellStyle,
  participantsTableHeaderStyle,
  participantsTableRowStyle,
  participantsTableScrollStyle,
  progressMiniRingWrapStyle,
  sectionBodyStyle,
  sectionCardStyle,
  sectionFootnoteStyle,
  sectionLinkStyle,
  sectionProgressStyle,
  sectionTitleStyle,
  statCardStyle,
  statIconWrapStyle,
  statLabelStyle,
  statSubLabelStyle,
  statusPillStyle,
  statsGridStyle,
  statValueStyle,
  titleStyle,
  topBarStyle,
  topBarActionsStyle,
} from "./styles";
import { AssessmentOverviewMetrics } from "./assessment-overview-metrics/assessment-overview-metrics";
import { AssessmentOverviewTopPerformers } from "./assessment-overview-top-performers/assessment-overview-top-performers";
import { isAxiosNetworkError } from "../../../utils/internetConnection";
import { AxiosError } from "axios";

const OFFLINE_DASHBOARD_MESSAGE =
  "No internet connection. Live dashboard will load when you're back online.";
const SERVER_DASHBOARD_ERROR_MESSAGE =
  "Failed to load live manager dashboard.";
const ASSESSMENT_NOT_FOUND_MESSAGE =
  "Contest not found. Live dashboard data is unavailable for this contest.";
const LIVE_STATUS_LABEL = "Live now";
const SCHEDULED_STATUS_LABEL = "Scheduled";
const FINISHED_STATUS_LABEL = "Finished";
const INACTIVE_STATUS_LABEL = "Offline";
const DASHBOARD_LIVE_POLL_MS = 20_000;
const DASHBOARD_FINISH_GRACE_MS = 10_000;
const DASHBOARD_GRACE_POLL_MS = 3_000;

const resolveDashboardFetchErrorMessage = (fetchError: unknown): string => {
  if (!navigator.onLine || isAxiosNetworkError(fetchError)) {
    return OFFLINE_DASHBOARD_MESSAGE;
  }

  if (fetchError instanceof AxiosError) {
    const status = fetchError.response?.status;
    const detail = fetchError.response?.data?.detail;
    const detailMessage =
      typeof detail === "string" && detail.trim()
        ? detail.trim()
        : Array.isArray(detail) &&
            typeof detail[0]?.msg === "string" &&
            detail[0].msg.trim()
          ? detail[0].msg.trim()
          : null;

    if (status === 404) {
      return detailMessage || ASSESSMENT_NOT_FOUND_MESSAGE;
    }

    if (status === 403) {
      return detailMessage || "You do not have access to this live dashboard.";
    }

    if (detailMessage) {
      return detailMessage;
    }
  }

  return SERVER_DASHBOARD_ERROR_MESSAGE;
};

interface AssessmentManagerDashboardProps {
  open: boolean;
  onClose: () => void;
  assessmentId: string | null;
  /** 0-based index for API `question_index` param */
  questionIndex: number;
  /** Short label for UI, e.g. Q9 */
  currentQuestionLabel?: string;
  dashboardType: "overview" | "questions";
  selectedFocus: string;
  questionPreviewByIndex?: Record<number, string>;
  questionPreviewSegmentsByIndex?: Record<number, QuestionPreviewSegment[]>;
}

export const formatQuestionShortLabel = (
  value: string | null | undefined,
  fallbackIndex?: number,
): string => {
  if (!value?.trim()) {
    return fallbackIndex != null ? `Q${fallbackIndex}` : "-";
  }

  const trimmed = value.trim();
  const shortMatch = trimmed.match(/^Q(\d+)$/i);
  if (shortMatch) {
    return `Q${Number(shortMatch[1])}`;
  }

  const questionWordMatch = trimmed.match(/^question\s*#?\s*(\d+)$/i);
  if (questionWordMatch) {
    return `Q${Number(questionWordMatch[1])}`;
  }

  if (/^\d+$/.test(trimmed)) {
    return `Q${Number(trimmed)}`;
  }

  const embeddedMatch = trimmed.match(/\bQ\s*(\d+)\b/i);
  if (embeddedMatch) {
    return `Q${Number(embeddedMatch[1])}`;
  }

  if (fallbackIndex != null) {
    return `Q${fallbackIndex}`;
  }

  return trimmed;
};

const resolveParticipantQuestionIndex = (
  participant: Pick<
    AssessmentManagerDashboardParticipant,
    "current_question_index" | "current_question"
  >,
): number | undefined => {
  if (
    typeof participant.current_question_index === "number" &&
    participant.current_question_index >= 0
  ) {
    return participant.current_question_index;
  }
  return undefined;
};

const AVATAR_COLORS = [
  "#3B82F6",
  "#06B6D4",
  "#22C55E",
  "#8B5CF6",
  "#F59E0B",
  "#EC4899",
];

const getParticipantInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
};

const getAvatarColor = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatLastActive = (value: string | null) => {
  if (!value) {
    return "No activity yet";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  const now = new Date();
  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  if (date.toDateString() === now.toDateString()) {
    return `Today, ${time}`;
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${time}`;
  }

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatParticipantStatusLabel = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === "completed") {
    return "Completed";
  }
  if (normalized === "in_progress") {
    return "In progress";
  }
  if (normalized === "not_started") {
    return "Not started";
  }
  return status.replace(/_/g, " ");
};

const ParticipantStatusPill = ({ status }: { status: string }) => {
  const normalized = status.toLowerCase();
  if (normalized === "completed") {
    return (
      <Box sx={participantStatusCompletedStyle}>
        <CheckCircle2 size={12} />
        Completed
      </Box>
    );
  }

  if (normalized === "not_started") {
    return (
      <Box sx={participantStatusNotStartedStyle}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "#6B7280",
          }}
        />
        {formatParticipantStatusLabel(status)}
      </Box>
    );
  }

  return (
    <Box sx={participantStatusInProgressStyle}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: "#0EA5E9",
        }}
      />
      {formatParticipantStatusLabel(status)}
    </Box>
  );
};

const statusColor = (status: string) => {
  if (status === "critical") return "#EF4444";
  if (status === "warning") return "#F59E0B";
  if (status === "in_progress") return "#0EA5E9";
  return "#22C55E";
};

const getPerformanceStatusHint = (status: string) => {
  switch (status) {
    case "critical":
      return "Critical: average team accuracy is below 60%. This question may need coaching or review.";
    case "warning":
      return "Needs attention: average team accuracy is between 60% and 84%.";
    case "in_progress":
      return "In progress: not enough responses yet to calculate a reliable status.";
    case "good":
    default:
      return "Strong performance: average team accuracy is 85% or higher.";
  }
};

const progressRingColor = (percent: number) => {
  if (percent >= 90) return "#22C55E";
  if (percent >= 75) return "#0EA5E9";
  if (percent >= 60) return "#F59E0B";
  return "#EF4444";
};

const PROGRESS_RING_SIZE = 56;
const PROGRESS_RING_SIZE_MOBILE = 46;
const PROGRESS_RING_THICKNESS = 5.5;
const PROGRESS_RING_THICKNESS_MOBILE = 5;

const QuestionProgressRing = ({
  percent,
  color,
}: {
  percent: number;
  color: string;
}) => {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const ringSize = isMobileLayout ? PROGRESS_RING_SIZE_MOBILE : PROGRESS_RING_SIZE;
  const ringThickness = isMobileLayout
    ? PROGRESS_RING_THICKNESS_MOBILE
    : PROGRESS_RING_THICKNESS;
  const value = Math.min(100, Math.max(0, percent));

  return (
    <Box sx={progressMiniRingWrapStyle}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={ringSize}
        thickness={ringThickness}
        sx={progressMiniRingTrackStyle}
      />
      <CircularProgress
        variant="determinate"
        value={value}
        size={ringSize}
        thickness={ringThickness}
        sx={progressMiniRingProgressStyle(color)}
      />
      <Typography sx={progressMiniRingLabelStyle(color)}>{value}%</Typography>
    </Box>
  );
};

const PERFORMANCE_STATUS_TOOLTIP_AUTO_CLOSE_MS = 10_000;

const PerformanceStatusIcon = ({
  status,
  isOpen,
  onToggle,
  onClose,
}: {
  status: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}) => {
  const color = statusColor(status);
  const hint = getPerformanceStatusHint(status);

  const icon =
    status === "critical" || status === "warning" ? (
      <AlertCircle size={14} />
    ) : status === "in_progress" ? (
      <LoaderCircle size={14} />
    ) : (
      <CheckCircle2 size={14} />
    );

  return (
    <Tooltip
      title={hint}
      open={isOpen}
      onClose={onClose}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      arrow
      placement="top"
    >
      <Box
        component="button"
        type="button"
        data-performance-status-trigger
        aria-label={hint}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        sx={{
          ...performanceStatusIconStyle(color),
          cursor: "pointer",
          border: "none",
          padding: 0,
          font: "inherit",
          transition: "background-color 0.15s ease, transform 0.15s ease",
          "&:hover": {
            backgroundColor: `${color}33`,
            transform: "scale(1.06)",
          },
        }}
      >
        {icon}
      </Box>
    </Tooltip>
  );
};

export const AssessmentManagerDashboard = ({
  open,
  onClose,
  assessmentId,
  questionIndex,
  currentQuestionLabel,
  dashboardType,
  selectedFocus,
  questionPreviewByIndex = {},
  questionPreviewSegmentsByIndex = {},
}: AssessmentManagerDashboardProps) => {
  const getFocusDisplayName = useUserStore((state) => state.getFocusDisplayName);
  const getQuestionLabel = useCallback(
    (
      value: AssessmentManagerDashboardQuestionRef | null | undefined,
      options?: {
        questionIndex?: number;
        fallbackLabel?: string;
      },
    ) => {
      const resolvedIndex =
        options?.questionIndex ?? resolveDashboardQuestionIndex(value);
      if (resolvedIndex !== undefined) {
        return formatAssessmentQuestionIndexLabel(resolvedIndex);
      }

      if (options?.fallbackLabel) {
        return options.fallbackLabel;
      }

      return "-";
    },
    [],
  );
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const [showAllPerformanceQuestions, setShowAllPerformanceQuestions] =
    useState(false);
  const [showAllAttentionItems, setShowAllAttentionItems] = useState(false);
  const [showAllParticipants, setShowAllParticipants] = useState(false);
  const [openedQuestionPreview, setOpenedQuestionPreview] = useState<{
    label: string;
    segments: QuestionPreviewSegment[];
  } | null>(null);
  const [openPerformanceStatusKey, setOpenPerformanceStatusKey] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOfflineError, setIsOfflineError] = useState(false);
  const [isNetworkOnline, setIsNetworkOnline] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [data, setData] = useState<AssessmentManagerDashboardData | null>(null);
  const [finishGraceActive, setFinishGraceActive] = useState(false);
  const wasOfflineRef = useRef<boolean>(
    typeof navigator === "undefined" ? false : !navigator.onLine,
  );
  const sawLiveAssessmentRef = useRef(false);
  const finishGraceTimerRef = useRef<number | null>(null);

  const resolvedQuestionIndex = useMemo(
    () => (Number.isFinite(questionIndex) && questionIndex >= 0 ? questionIndex : 0),
    [questionIndex],
  );

  const clearFinishGraceTimer = useCallback(() => {
    if (finishGraceTimerRef.current != null) {
      window.clearTimeout(finishGraceTimerRef.current);
      finishGraceTimerRef.current = null;
    }
  }, []);

  const stopFinishGrace = useCallback(() => {
    clearFinishGraceTimer();
    setFinishGraceActive(false);
  }, [clearFinishGraceTimer]);

  const fetchData = useCallback(async (opts?: { silent?: boolean }): Promise<boolean | null> => {
    if (!assessmentId) {
      return null;
    }
    if (!Number.isFinite(resolvedQuestionIndex) || resolvedQuestionIndex < 0) {
      setData(null);
      setError(null);
      setIsOfflineError(false);
      setLoading(false);
      return null;
    }
    if (!navigator.onLine) {
      setData(null);
      setError(OFFLINE_DASHBOARD_MESSAGE);
      setIsOfflineError(true);
      setLoading(false);
      return null;
    }
    if (!opts?.silent) {
      setLoading(true);
    }
    setError(null);
    setIsOfflineError(false);
    try {
      const response = await getAssessmentManagerDashboard(
        assessmentId,
        resolvedQuestionIndex,
      );

      if (!response?.assessment?.id) {
        setData(null);
        setError(ASSESSMENT_NOT_FOUND_MESSAGE);
        setIsOfflineError(false);
        return null;
      }

      setData({
        ...response,
        summary: response.summary ?? {
          invited: 0,
          completed: 0,
          in_progress: 0,
          not_started: 0,
          completion_percent: 0,
          questions_count: 0,
        },
        questions: {
          ...response.questions,
          progress: response.questions?.progress ?? [],
          performance: response.questions?.performance ?? [],
        },
        participants: {
          items: response.participants?.items ?? [],
          recent_activity: response.participants?.recent_activity ?? [],
        },
        attention: response.attention ?? { items: [] },
      });
      return response.assessment.is_finished === true;
    } catch (fetchError) {
      console.error("Error loading manager dashboard data:", fetchError);
      const offline =
        !navigator.onLine || isAxiosNetworkError(fetchError);
      // Keep previous data on silent poll failures so the view does not flash empty.
      if (!opts?.silent) {
        setError(
          offline
            ? OFFLINE_DASHBOARD_MESSAGE
            : resolveDashboardFetchErrorMessage(fetchError),
        );
        setIsOfflineError(offline);
        setData(null);
      } else if (offline) {
        setIsNetworkOnline(false);
      }
      return null;
    } finally {
      if (!opts?.silent) {
        setLoading(false);
      }
    }
  }, [assessmentId, resolvedQuestionIndex]);

  const startFinishGrace = useCallback(() => {
    setFinishGraceActive(true);
    clearFinishGraceTimer();
    finishGraceTimerRef.current = window.setTimeout(() => {
      finishGraceTimerRef.current = null;
      void fetchData({ silent: true }).finally(() => {
        setFinishGraceActive(false);
      });
    }, DASHBOARD_FINISH_GRACE_MS);
  }, [clearFinishGraceTimer, fetchData]);

  const handleManualRefresh = useCallback(async () => {
    const finished = await fetchData();
    if (finished === true && sawLiveAssessmentRef.current) {
      startFinishGrace();
    }
  }, [fetchData, startFinishGrace]);

  useEffect(() => {
    const syncNetworkStatus = () => {
      setIsNetworkOnline(navigator.onLine);
    };

    const handleOnline = () => setIsNetworkOnline(true);
    const handleOffline = () => setIsNetworkOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const pollId = window.setInterval(syncNetworkStatus, 1500);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.clearInterval(pollId);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setShowAllPerformanceQuestions(false);
      setShowAllAttentionItems(false);
      setShowAllParticipants(false);
      setOpenPerformanceStatusKey(null);
      setData(null);
      setError(null);
      setIsOfflineError(false);
      stopFinishGrace();
      sawLiveAssessmentRef.current = false;
      return;
    }
    void fetchData();
  }, [open, fetchData, stopFinishGrace]);

  useEffect(() => {
    if (!isNetworkOnline) {
      wasOfflineRef.current = true;
      return;
    }

    if (!open || !wasOfflineRef.current || !isOfflineError) {
      return;
    }

    wasOfflineRef.current = false;
    void fetchData();
  }, [open, isNetworkOnline, isOfflineError, fetchData]);

  const hasDashboardPayload = Boolean(data);
  const backendAssessmentFinished = data?.assessment.is_finished === true;
  const backendAssessmentLive =
    data?.assessment.is_live === true && !backendAssessmentFinished;
  const backendAssessmentScheduled =
    data?.assessment.is_scheduled === true &&
    !backendAssessmentFinished &&
    !backendAssessmentLive;

  useEffect(() => {
    if (!open || !hasDashboardPayload) {
      return;
    }

    if (backendAssessmentLive) {
      sawLiveAssessmentRef.current = true;
      stopFinishGrace();
      return;
    }

    if (!backendAssessmentFinished) {
      // Scheduled / not yet started — do not arm live→finished grace.
      stopFinishGrace();
      return;
    }

    // Opened already finished — no grace delay (including manual refresh).
    if (!sawLiveAssessmentRef.current) {
      stopFinishGrace();
      return;
    }

    startFinishGrace();
  }, [
    open,
    hasDashboardPayload,
    backendAssessmentFinished,
    backendAssessmentLive,
    startFinishGrace,
    stopFinishGrace,
  ]);

  useEffect(() => {
    if (!open || !isNetworkOnline || !hasDashboardPayload) {
      return;
    }
    if (backendAssessmentFinished && !finishGraceActive) {
      return;
    }

    const pollMs = finishGraceActive
      ? DASHBOARD_GRACE_POLL_MS
      : DASHBOARD_LIVE_POLL_MS;

    if (finishGraceActive) {
      void fetchData({ silent: true });
    }

    const pollId = window.setInterval(() => {
      void fetchData({ silent: true });
    }, pollMs);

    return () => {
      window.clearInterval(pollId);
    };
  }, [
    open,
    isNetworkOnline,
    hasDashboardPayload,
    backendAssessmentFinished,
    finishGraceActive,
    fetchData,
  ]);

  useEffect(() => {
    if (!openPerformanceStatusKey) {
      return;
    }

    const timer = window.setTimeout(() => {
      setOpenPerformanceStatusKey(null);
    }, PERFORMANCE_STATUS_TOOLTIP_AUTO_CLOSE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [openPerformanceStatusKey]);

  useEffect(() => {
    if (!openPerformanceStatusKey) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      if (target.closest("[data-performance-status-trigger]")) {
        return;
      }

      if (target.closest(".MuiTooltip-popper")) {
        return;
      }

      setOpenPerformanceStatusKey(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [openPerformanceStatusKey]);

  const questionProgress = useMemo(
    () => data?.questions?.progress ?? [],
    [data?.questions?.progress],
  );

  const currentQuestionSnapshot =
    useMemo((): AssessmentManagerDashboardQuestionSnapshot | null => {
      if (!data) {
        return null;
      }

      if (data.questions?.current) {
        return data.questions.current;
      }

      return {
        question_index: resolvedQuestionIndex,
        question: formatAssessmentQuestionIndexLabel(resolvedQuestionIndex),
        answered_count: 0,
        invited_count: data.summary?.invited ?? 0,
        answered_percent: 0,
        average_score: 0,
        max_score: 0,
        average_bonus_score: 0,
        accuracy: 0,
      };
    }, [data, resolvedQuestionIndex]);

  const recentParticipants = useMemo(
    () => data?.participants?.recent_activity ?? [],
    [data],
  );
  const visibleParticipants = useMemo(() => {
    return showAllParticipants
      ? recentParticipants
      : recentParticipants.slice(0, 5);
  }, [recentParticipants, showAllParticipants]);
  const hasMoreParticipants = recentParticipants.length > 5;
  const performanceQuestions = useMemo(() => {
    const all = data?.questions?.performance ?? [];
    return showAllPerformanceQuestions ? all : all.slice(0, 5);
  }, [data, showAllPerformanceQuestions]);
  const hasMorePerformanceQuestions =
    (data?.questions?.performance?.length ?? 0) > 5;
  const attentionItems = useMemo(
    () => data?.attention?.items ?? [],
    [data?.attention?.items],
  );
  const visibleAttentionItems = useMemo(() => {
    return showAllAttentionItems ? attentionItems : attentionItems.slice(0, 3);
  }, [attentionItems, showAllAttentionItems]);
  const hasMoreAttentionItems = attentionItems.length > 3;
  const resolveQuestionPreviewContent = useCallback(
    (
      rawQuestion: AssessmentManagerDashboardQuestionRef,
      questionIndex?: number,
    ): QuestionPreviewSegment[] => {
      const resolvedIndex =
        questionIndex ?? resolveDashboardQuestionIndex(rawQuestion);

      const localSegments =
        resolvedIndex !== undefined
          ? questionPreviewSegmentsByIndex[resolvedIndex]
          : undefined;
      if (localSegments?.length) {
        return localSegments;
      }

      const segments = resolveDashboardQuestionPreviewContent(rawQuestion);
      const joined = segments
        .map((segment: QuestionPreviewSegment) => segment.text)
        .join("");
      if (joined.trim() && !/^Q\d+$/i.test(joined)) {
        return segments;
      }

      const previewText =
        resolvedIndex !== undefined
          ? questionPreviewByIndex[resolvedIndex]
          : undefined;
      if (previewText?.trim() && !/\{\{\w+\}\}/.test(previewText)) {
        return [{ text: previewText }];
      }

      return [{ text: "Full question text unavailable." }];
    },
    [questionPreviewByIndex, questionPreviewSegmentsByIndex],
  );

  const getAttentionBadge = useCallback(
    (item: (typeof attentionItems)[number]) => {
      const reason = item.reason?.trim();
      if (reason) {
        return reason;
      }
      const questionIndex = resolveParticipantQuestionIndex(item);
      if (questionIndex !== undefined) {
        return `Stuck on ${formatAssessmentQuestionIndexLabel(questionIndex)}`;
      }
      if (item.current_question) {
        return `Stuck on ${formatQuestionShortLabel(item.current_question)}`;
      }
      return formatParticipantStatusLabel(item.status);
    },
    [],
  );

  const getAttentionBadgeStyle = useCallback(
    (item: (typeof attentionItems)[number]) => {
      const text = getAttentionBadge(item).toLowerCase();
      if (text.includes("inactive")) {
        return attentionBadgeInactiveStyle;
      }
      return attentionBadgeStuckStyle;
    },
    [getAttentionBadge],
  );

  const isAssessmentFinished =
    backendAssessmentFinished && !finishGraceActive;
  const isAssessmentScheduled =
    !isAssessmentFinished && backendAssessmentScheduled;
  const hasDashboardData = Boolean(data) && !loading && !error;

  const dashboardTitle = isAssessmentFinished
    ? "TAP Challenge Dashboard 🏆"
    : isAssessmentScheduled
      ? "TAP Challenge Scheduled Dashboard 🏆"
      : "TAP Challenge Live Dashboard 🏆";

  const liveStatusLabel = !isNetworkOnline || !hasDashboardData
    ? INACTIVE_STATUS_LABEL
    : isAssessmentFinished
      ? FINISHED_STATUS_LABEL
      : isAssessmentScheduled
        ? SCHEDULED_STATUS_LABEL
        : LIVE_STATUS_LABEL;

  const isDarkTheme = theme.palette.mode === "dark";
  const isAssessmentLive =
    hasDashboardData &&
    isNetworkOnline &&
    !isAssessmentFinished &&
    !isAssessmentScheduled;

  const liveStatusPillSx = useMemo(
    () =>
      [
        statusPillStyle,
        isAssessmentLive && {
          color: isDarkTheme ? "#86EFAC" : "#166534",
          border: isDarkTheme
            ? "1px solid rgba(134,239,172,0.35)"
            : "1px solid rgba(22,163,74,0.22)",
        },
        isAssessmentFinished &&
          hasDashboardData &&
          isNetworkOnline && {
            color: isDarkTheme ? "#94A3B8" : "#64748B",
            border: "1px solid rgba(100,116,139,0.28)",
          },
        isAssessmentScheduled &&
          hasDashboardData &&
          isNetworkOnline && {
            color: isDarkTheme ? "#7DD3FC" : "#0369A1",
            border: isDarkTheme
              ? "1px solid rgba(125,211,252,0.35)"
              : "1px solid rgba(14,165,233,0.28)",
          },
        (!isNetworkOnline || !hasDashboardData) && {
          color: "#B91C1C",
          border: "1px solid rgba(185,28,28,0.22)",
        },
      ] as SxProps<Theme>,
    [
      isAssessmentLive,
      isAssessmentFinished,
      isAssessmentScheduled,
      hasDashboardData,
      isNetworkOnline,
      isDarkTheme,
    ],
  );

  const liveStatusDotSx = useMemo(() => {
    const isOffline = !isNetworkOnline || !hasDashboardData;
    const color = isOffline
      ? "#DC2626"
      : isAssessmentFinished
        ? "#94A3B8"
        : isAssessmentScheduled
          ? "#0EA5E9"
          : isDarkTheme
            ? "#86EFAC"
            : "#16A34A";
    const glow = isOffline
      ? "0 0 0 4px rgba(220,38,38,0.14)"
      : isAssessmentFinished
        ? "0 0 0 4px rgba(148,163,184,0.18)"
        : isAssessmentScheduled
          ? "0 0 0 4px rgba(14,165,233,0.14)"
          : isDarkTheme
            ? "0 0 0 4px rgba(134,239,172,0.18)"
            : "0 0 0 4px rgba(22,163,74,0.14)";
    return {
      width: 9,
      height: 9,
      borderRadius: "50%",
      backgroundColor: color,
      boxShadow: glow,
    };
  }, [
    isAssessmentFinished,
    isAssessmentScheduled,
    hasDashboardData,
    isNetworkOnline,
    isDarkTheme,
  ]);

  if (!open) {
    return null;
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        hideBackdrop
        sx={{
          zIndex: 1400,
        }}
      >
        <Box sx={modalStyle}>
          {isMobileLayout ? (
            <>
              <Box sx={topBarStyle}>
                <Typography sx={titleStyle}>
                  {dashboardTitle}
                </Typography>
              </Box>
              <Box sx={topBarStyle}>
                <Typography sx={metaTextStyle}>
                  Updated{" "}
                  {data?.assessment?.updated_at
                    ? formatLastActive(data.assessment.updated_at)
                    : "-"}
                </Typography>
                <Box sx={topBarActionsStyle}>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: 36,
                      width: 36,
                      p: 0.75,
                      borderRadius: "999px",
                    }}
                    onClick={() => void handleManualRefresh()}
                    disabled={loading}
                    aria-label="Refresh list"
                  >
                    <RefreshCcw size={14} />
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<X size={14} />}
                    onClick={onClose}
                    sx={{ borderRadius: "999px" }}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={topBarStyle}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Typography sx={titleStyle}>
                  {dashboardTitle}
                </Typography>
                <Box sx={liveStatusPillSx}>
                  <Box sx={liveStatusDotSx} />
                  {liveStatusLabel}
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={metaTextStyle}>
                  Updated{" "}
                  {data?.assessment?.updated_at
                    ? formatLastActive(data.assessment.updated_at)
                    : "-"}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: 36,
                    width: 36,
                    p: 0.75,
                    borderRadius: "999px",
                  }}
                  onClick={() => void handleManualRefresh()}
                  disabled={loading}
                  aria-label="Refresh list"
                >
                  <RefreshCcw size={14} />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<X size={14} />}
                  onClick={onClose}
                  sx={{ borderRadius: "999px" }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          )}

          <Box sx={topBarStyle}>
            <Box sx={assessmentFocusLabelStyle}>
              <Focus size={16} color="#0EA5E9" />
              <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                {data?.assessment?.title?.trim()
                  ? data.assessment.title.trim()
                  : selectedFocus
                    ? `${getFocusDisplayName(selectedFocus)} Knowledge Challenge`
                    : "Knowledge Challenge"}
              </Typography>
            </Box>
            <Box />
          </Box>

          {isMobileLayout ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 1.2,
                pr: { xs: 1, md: 2.5 },
              }}
            >
              <Box sx={liveStatusPillSx}>
                <Box sx={liveStatusDotSx} />
                {liveStatusLabel}
              </Box>
            </Box>
          ) : null}

          {loading ? (
            <Box
              sx={{
                minHeight: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : null}

          {!loading && error ? (
            <Alert
              severity={isOfflineError ? "warning" : "error"}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void handleManualRefresh()}
                  disabled={loading}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          ) : null}

          {!loading && !error && !data ? (
            <Box
              sx={{
                minHeight: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
              }}
            >
              <Typography
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                  fontSize: 14,
                }}
              >
                Live dashboard will populate once participants start the
                challenge.
              </Typography>
            </Box>
          ) : null}

          {!loading && !error && data ? (
            <>
              <Box sx={statsGridStyle}>
                <Box sx={statCardStyle}>
                  <Box sx={statIconWrapStyle("#0EA5E9")}>
                    <Users size={26} />
                  </Box>
                  <Box>
                    <Typography sx={statLabelStyle}>Team invited</Typography>
                    <Typography sx={statValueStyle}>
                      {data.summary.invited}
                    </Typography>
                    <Typography sx={statSubLabelStyle}>members</Typography>
                  </Box>
                </Box>
                <Box sx={statCardStyle}>
                  <Box sx={statIconWrapStyle("#22C55E")}>
                    <CheckCircle2 size={26} />
                  </Box>
                  <Box>
                    <Typography sx={statLabelStyle}>Completed</Typography>
                    <Typography sx={statValueStyle}>
                      {data.summary.completed}
                    </Typography>
                    <Typography sx={statSubLabelStyle}>
                      {data.summary.completion_percent}% of invited
                    </Typography>
                  </Box>
                </Box>
                <Box sx={statCardStyle}>
                  <Box sx={statIconWrapStyle("#7C3AED")}>
                    <LoaderCircle size={26} />
                  </Box>
                  <Box>
                    <Typography sx={statLabelStyle}>In progress</Typography>
                    <Typography sx={statValueStyle}>
                      {data.summary.in_progress}
                    </Typography>
                    <Typography sx={statSubLabelStyle}>
                      {Math.round(
                        (data.summary.in_progress /
                          Math.max(1, data.summary.invited)) *
                          100,
                      )}
                      % of invited
                    </Typography>
                  </Box>
                </Box>
                <Box sx={statCardStyle}>
                  <Box sx={statIconWrapStyle("#374151")}>
                    <UserRound size={26} />
                  </Box>
                  <Box>
                    <Typography sx={statLabelStyle}>Not started</Typography>
                    <Typography sx={statValueStyle}>
                      {data.summary.not_started}
                    </Typography>
                    <Typography sx={statSubLabelStyle}>
                      {Math.round(
                        (data.summary.not_started /
                          Math.max(1, data.summary.invited)) *
                          100,
                      )}
                      % of invited
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {dashboardType === "overview" ? (
                <Box sx={overviewMiddleRowStyle}>
                  <AssessmentOverviewMetrics
                    summary={data.summary}
                    teamPerformance={data.team_performance}
                  />
                  <AssessmentOverviewTopPerformers
                    performers={
                      data.leaderboard?.top_performers?.length
                        ? data.leaderboard.top_performers
                        : (data.leaderboard?.items ?? [])
                    }
                    maxScore={
                      data.team_performance?.mandatory_max_score ??
                      data.team_performance?.max_score ??
                      0
                    }
                  />
                </Box>
              ) : (
                <Box sx={dashboardGridStyle}>
                  <Box sx={sectionCardStyle}>
                    <Typography sx={sectionTitleStyle}>
                      Live question progress
                    </Typography>
                    <Box sx={sectionBodyStyle}>
                      <Box sx={progressMiniScrollWrapStyle}>
                        <Box
                          sx={{
                            ...progressMiniGridStyle,
                            gridTemplateColumns: `repeat(${Math.max(questionProgress.length, 1)}, minmax(0, 1fr))`,
                            width:
                              questionProgress.length <= 6
                                ? "100%"
                                : `${(questionProgress.length / 6) * 100}%`,
                          }}
                        >
                        {questionProgress.map((item, index) => {
                          const ringColor = progressRingColor(
                            item.answered_percent,
                          );
                          const itemQuestionIndex =
                            item.question_index ?? index;
                          return (
                            <Box
                              key={`question-progress-${itemQuestionIndex}`}
                              sx={progressMiniCardStyle}
                            >
                              <Typography sx={performanceQuestionStyle}>
                                {getQuestionLabel(item.question, {
                                  questionIndex: itemQuestionIndex,
                                })}
                              </Typography>
                              <QuestionProgressRing
                                percent={item.answered_percent}
                                color={ringColor}
                              />
                            </Box>
                          );
                        })}
                        </Box>
                      </Box>
                      <Typography sx={sectionFootnoteStyle}>
                        % of participants answered
                      </Typography>
                    </Box>
                </Box>

                <Box sx={sectionCardStyle}>
                  <Typography sx={sectionTitleStyle}>
                    Question performance
                  </Typography>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "text.secondary",
                        mb: 0.6,
                      }}
                    >
                      Click question label to open full text.
                    </Typography>
                    <Box sx={sectionBodyStyle}>
                      <Box sx={performanceTableHeaderStyle}>
                        <Typography sx={performanceTableHeaderCellStyle}>
                          Avg accuracy
                        </Typography>
                        <Typography
                          sx={{
                            ...performanceTableHeaderCellStyle,
                            textAlign: "right",
                          }}
                        />
                        <Typography sx={performanceTableHeaderCellStyle}>
                          Status
                        </Typography>
                      </Box>
                      {performanceQuestions.map((item, index) => {
                        const barColor = statusColor(item.status);
                        const isHighlighted = item.status === "critical";
                        const itemQuestionIndex =
                          item.question_index ?? index;
                        const questionLabel = getQuestionLabel(item.question, {
                          questionIndex: itemQuestionIndex,
                        });
                        const statusTooltipKey = `performance-status-${index}`;
                        return (
                          <Box
                            key={`question-performance-${itemQuestionIndex}`}
                            sx={
                              (isHighlighted
                                ? [
                                    performanceRowStyle,
                                    performanceRowHighlightStyle,
                                  ]
                                : performanceRowStyle) as SxProps<Theme>
                            }
                          >
                            <Box sx={performanceQuestionTopRowStyle}>
                              <Typography
                                sx={{
                                  ...performanceQuestionStyle,
                                  cursor: "pointer",
                                  "&:hover": {
                                    textDecoration: "underline",
                                  },
                                }}
                                onClick={() =>
                                  setOpenedQuestionPreview({
                                    label: questionLabel,
                                    segments: resolveQuestionPreviewContent(
                                      item.question,
                                      itemQuestionIndex,
                                    ),
                                  })
                                }
                              >
                                {questionLabel}
                              </Typography>
                              <Chip
                                size="small"
                                label={item.is_bonus ? "Bonus" : "Mandatory"}
                                color={item.is_bonus ? "warning" : "default"}
                                sx={performanceTypeChipStyle}
                              />
                            </Box>
                            <Box sx={performanceMetricsRowStyle}>
                              <Box sx={performanceBarTrackStyle}>
                                <Box
                                  sx={performanceBarFillStyle(barColor)}
                                  style={{ width: `${item.accuracy}%` }}
                                />
                              </Box>
                              <Typography sx={performancePercentStyle}>
                                {item.accuracy}%
                              </Typography>
                              <PerformanceStatusIcon
                                status={item.status}
                                isOpen={
                                  openPerformanceStatusKey === statusTooltipKey
                                }
                                onToggle={() =>
                                  setOpenPerformanceStatusKey((prev) =>
                                    prev === statusTooltipKey
                                      ? null
                                      : statusTooltipKey,
                                  )
                                }
                                onClose={() =>
                                  setOpenPerformanceStatusKey(null)
                                }
                              />
                            </Box>
                          </Box>
                        );
                      })}
                      {hasMorePerformanceQuestions ? (
                        <Typography
                          sx={sectionLinkStyle}
                          onClick={() =>
                            setShowAllPerformanceQuestions((prev) => !prev)
                          }
                        >
                          {showAllPerformanceQuestions
                            ? "Show less questions"
                            : "View all questions"}
                          <ChevronRight
                            size={14}
                            style={{
                              transform: showAllPerformanceQuestions
                                ? "rotate(90deg)"
                                : "none",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>

                  {currentQuestionSnapshot ? (
                    <Box sx={currentQuestionCardStyle}>
                      <Typography sx={sectionTitleStyle}>
                        Current question
                      </Typography>
                      <Box sx={sectionBodyStyle}>
                        <Typography sx={currentQuestionTitleStyle}>
                          {getQuestionLabel(currentQuestionSnapshot.question, {
                            questionIndex:
                              currentQuestionSnapshot.question_index ??
                              resolvedQuestionIndex,
                            fallbackLabel: currentQuestionLabel,
                          })}
                        </Typography>
                        <Box sx={currentQuestionMetaStyle}>
                          <Users size={14} />
                          <span>
                            {currentQuestionSnapshot.answered_count} of{" "}
                            {currentQuestionSnapshot.invited_count} answered
                          </span>
                        </Box>
                        <Box sx={metricsRowStyle}>
                          <Box sx={metricMiniCardStyle}>
                            <Box sx={metricMiniCardIconWrapStyle}>
                              <Star size={16} />
                            </Box>
                            <Box>
                              <Typography sx={metricMiniCardLabelStyle}>
                                Avg score
                              </Typography>
                              <Typography sx={metricMiniCardValueStyle}>
                                <AssessmentScoreInline
                                  score={currentQuestionSnapshot.average_score}
                                  maxScore={currentQuestionSnapshot.max_score}
                                  bonusScore={
                                    currentQuestionSnapshot.average_bonus_score
                                  }
                                />
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={metricMiniCardStyle}>
                            <Box sx={metricMiniCardIconWrapSuccessStyle}>
                              <Target size={16} />
                            </Box>
                            <Box>
                              <Typography sx={metricMiniCardLabelStyle}>
                                Accuracy
                              </Typography>
                              <Typography sx={metricMiniCardValueStyle}>
                                {currentQuestionSnapshot.accuracy}%
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={
                            currentQuestionSnapshot.invited_count > 0
                              ? (currentQuestionSnapshot.answered_count /
                                  currentQuestionSnapshot.invited_count) *
                                100
                              : 0
                          }
                          sx={sectionProgressStyle}
                        />
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              )}

              <Box sx={bottomSectionGridStyle}>
                <Box sx={bottomPanelStyle}>
                  <Box sx={bottomPanelHeaderStyle}>
                    <Typography sx={bottomPanelTitleStyle}>
                      Participants{" "}
                      <Box component="span" sx={bottomPanelTitleMutedStyle}>
                        (recent activity)
                      </Box>
                    </Typography>
                  </Box>
                  <Box sx={participantsTableScrollStyle}>
                  <Box sx={participantsTableHeaderStyle}>
                    <Typography sx={participantsTableHeaderCellStyle}>
                      Participant
                    </Typography>
                    <Typography sx={participantsTableHeaderCellStyle}>
                      Status
                    </Typography>
                    <Typography sx={participantsTableHeaderCellStyle}>
                      Current question
                    </Typography>
                    <Typography sx={participantsTableHeaderCellStyle}>
                      Mandatory
                    </Typography>
                    <Typography sx={participantsTableHeaderCellStyle}>
                      Bonus questions
                    </Typography>
                    <Typography sx={participantsTableHeaderCellStyle}>
                      Total user score
                    </Typography>
                    <Typography sx={participantsTableHeaderCellStyle}>
                      Gamification
                    </Typography>
                    <Typography sx={participantsTableHeaderCellStyle}>
                      Last active
                    </Typography>
                  </Box>
                  {recentParticipants.length === 0 ? (
                    <Box sx={{ px: 1.5, py: 2 }}>
                      <Typography sx={participantMutedCellStyle}>
                        No recent participant activity yet.
                      </Typography>
                    </Box>
                  ) : (
                    visibleParticipants.map(
                      (item: AssessmentManagerDashboardParticipant) => {
                        const mandatoryScore = formatAssessmentScoreNumber(
                          item.mandatory_score ??
                            getAssessmentBaseScore(
                              item.score,
                              item.bonus_score ?? 0,
                            ),
                        );
                        const bonusQuestionScore = formatAssessmentScoreNumber(
                          item.bonus_question_score ?? 0,
                        );
                        const userScore = formatAssessmentScoreNumber(
                          item.user_score ??
                            getAssessmentBaseScore(
                              item.score,
                              item.bonus_score ?? 0,
                            ),
                        );
                        const gamificationScore = formatAssessmentScoreNumber(
                          item.gamification_bonus_score ??
                            item.bonus_score ??
                            0,
                        );
                        const questionIndex =
                          resolveParticipantQuestionIndex(item);
                        const currentQuestionLabel =
                          questionIndex !== undefined
                            ? formatAssessmentQuestionIndexLabel(questionIndex)
                            : item.current_question
                              ? formatQuestionShortLabel(item.current_question)
                              : "-";

                        return (
                          <Box key={item.user_id} sx={participantsTableRowStyle}>
                            <Box sx={participantIdentityStyle}>
                              <Box
                                sx={participantAvatarStyle(
                                  getAvatarColor(item.user_id),
                                )}
                              >
                                {getParticipantInitials(item.name)}
                              </Box>
                              <Typography sx={participantNameStyle}>
                                {item.name}
                              </Typography>
                            </Box>
                            <Box>
                              <ParticipantStatusPill status={item.status} />
                            </Box>
                            <Typography sx={participantCellStyle}>
                              {currentQuestionLabel}
                            </Typography>
                            <Box
                              sx={{
                                ...participantCellStyle,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1px",
                                lineHeight: 1.2,
                              }}
                            >
                              <Box component="span">{mandatoryScore}</Box>
                            </Box>
                            <Typography sx={participantCellStyle}>
                              {bonusQuestionScore}
                            </Typography>
                            <Typography sx={participantCellStyle}>
                              {userScore}
                            </Typography>
                            <Typography sx={participantCellStyle}>
                              {gamificationScore}
                            </Typography>
                            <Typography sx={participantMutedCellStyle}>
                              {formatLastActive(item.last_active)}
                            </Typography>
                          </Box>
                        );
                      },
                    )
                  )}
                  </Box>
                  {hasMoreParticipants ? (
                    <Box sx={bottomPanelFooterStyle}>
                      <Typography
                        sx={sectionLinkStyle}
                        onClick={() => setShowAllParticipants((prev) => !prev)}
                      >
                        {showAllParticipants ? "Show less" : "View all"}
                        <ChevronRight
                          size={14}
                          style={{
                            transform: showAllParticipants
                              ? "rotate(90deg)"
                              : "none",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </Typography>
                    </Box>
                  ) : null}
                </Box>

                <Box sx={needsAttentionPanelCombinedStyle}>
                  <Box sx={needsAttentionHeaderStyle}>
                    <Typography sx={bottomPanelTitleStyle}>
                      Needs attention
                    </Typography>
                  </Box>
                  <Box sx={attentionListStyle}>
                    {visibleAttentionItems.length === 0 ? (
                      <Typography sx={participantMutedCellStyle}>
                        No participants need attention right now.
                      </Typography>
                    ) : (
                      visibleAttentionItems.map((item) => (
                        <Box key={item.user_id} sx={needsAttentionRowStyle}>
                          <Box sx={participantIdentityStyle}>
                            <Box
                              sx={participantAvatarStyle(
                                getAvatarColor(item.user_id),
                              )}
                            >
                              {getParticipantInitials(item.name)}
                            </Box>
                            <Typography sx={participantNameStyle}>
                              {item.name}
                            </Typography>
                          </Box>
                          <Typography sx={getAttentionBadgeStyle(item)}>
                            {getAttentionBadge(item)}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                  {hasMoreAttentionItems ? (
                    <Box sx={bottomPanelFooterStyle}>
                      <Typography
                        sx={sectionLinkStyle}
                        onClick={() =>
                          setShowAllAttentionItems((prev) => !prev)
                        }
                      >
                        {showAllAttentionItems ? "Show less" : "View all"}
                        <ChevronRight
                          size={14}
                          style={{
                            transform: showAllAttentionItems
                              ? "rotate(90deg)"
                              : "none",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </>
          ) : null}
        </Box>
      </Modal>
      <Dialog
        open={Boolean(openedQuestionPreview)}
        onClose={() => setOpenedQuestionPreview(null)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1700 }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <span>{openedQuestionPreview?.label ?? "Question"}</span>
          <IconButton
            size="small"
            aria-label="Close question preview"
            onClick={() => setOpenedQuestionPreview(null)}
            sx={{ borderRadius: "999px" }}
          >
            <X size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography
            component="div"
            sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}
          >
            {openedQuestionPreview?.segments.map((segment, index) =>
              segment.bold ? (
                <Box
                  component="span"
                  key={`preview-segment-${index}`}
                  sx={{ fontWeight: 800 }}
                >
                  {segment.text}
                </Box>
              ) : (
                <span key={`preview-segment-${index}`}>{segment.text}</span>
              ),
            )}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssessmentManagerDashboard;
